import dayjs from 'dayjs'
import { appGetState } from 'legacy/lut/store'

import { IDataProviderFilter, IDataProviderSort, Logic, Operator } from 'core-components/types'
import { toServerFormat } from 'core/classes/helpers/DateHelper'
import { CrudHelper } from 'core/types/helpers/CrudHelper'

import IUserEventStorage from './IUserEventStorage'
import { create as createUserEvent, IUserLevel, IModel as TUserEvent } from './UserEvent'

class UserEventStorageServer implements IUserEventStorage {
  protected crud: CrudHelper
  protected crudLevel: CrudHelper
  protected crudAchievement: CrudHelper
  protected appGetState = appGetState
  protected processing: boolean

  constructor() {
    this.processing = false
    this.crud = new CrudHelper(311, 'UsageStatistic')
    this.crudLevel = new CrudHelper(457, 'GamificationLevels')
    this.crudAchievement = new CrudHelper(261, 'TheMostAchievement')
  }

  public async fetchAchievement(): Promise<number> {
    const today = new Date().toISOString().split('T')[0] + 'T00:00:00'
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [
        {
          field: 'Level',
          operator: Operator.GT,
          value: '15',
        },
        {
          field: 'Date',
          operator: Operator.EQUAL,
          value: today,
        },
      ],
    }
    const response =
      (await this.crudLevel.read(filter, undefined, 1, 6, undefined, false, true)) || {}
    return (response['Data'] && response['Data'].length) || 0
  }

  public async fetchLevel(email: string): Promise<IUserLevel> {
    const today = new Date().toISOString().split('T')[0] + 'T00:00:00'
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [
        {
          field: 'Email',
          operator: Operator.EQUAL,
          value: email,
        },
        {
          field: 'AppId',
          operator: Operator.EQUAL,
          value: this.appGetState().app.id,
        },
        {
          field: 'Date',
          operator: Operator.EQUAL,
          value: today,
        },
      ],
    }

    const response =
      (await this.crudLevel.read(filter, undefined, 1, 1, undefined, false, true)) || {}
    const row = (response['Data'][0] as IObjectAny[]) || { Level: 0 }
    return {
      EntryId: row['EntryId'],
      Email: row['Email'],
      Level: parseInt(row['Level']) || 0,
    }
  }

  public async addLevel(user: IUserLevel): Promise<IUserLevel> {
    const today = new Date().toISOString().split('T')[0] + 'T00:00:00'
    const response = await this.crudLevel.create([
      {
        Date: today,
        Email: user.Email,
        Level: user.Level,
        AppId: this.appGetState().app.id,
        AudienceFilter: `AppId='${this.appGetState().app.id}' and Email='${user.Email}'`,
      },
    ])
    if (response.length) {
      const row = response[0] as IObjectAny
      return {
        EntryId: row['EntryId'],
        Email: row['Email'],
        Level: parseInt(row['Level']) || 0,
      }
    }
    throw new Error(
      `Cannot added user level: ${JSON.stringify({
        Email: user.Email,
        Level: user.Level,
      })}`
    )
  }

  public async updateLevel(user: IUserLevel): Promise<IUserLevel | undefined> {
    if (this.processing) {
      return
    }
    this.processing = true
    const response = await this.crudLevel.update([
      {
        EntryId: user.EntryId,
        Level: user.Level,
      },
    ])
    this.processing = false
    if (response.length) {
      const row = response[0] as IObjectAny
      return {
        EntryId: row['EntryId'],
        Email: row['Email'],
        Level: parseInt(row['Level']) || 0,
      }
    }
    throw new Error(
      `Cannot update user current level: ${JSON.stringify({
        EntryId: user.EntryId,
        Level: user.Level,
      })}`
    )
  }

  public async fetch(email: string): Promise<TUserEvent[]> {
    const fromDateTime = dayjs().add(-30, 'day').format('YYYY-MM-DD hh:mm')
    const serverDateTime = toServerFormat(fromDateTime)
    const filter: IDataProviderFilter = {
      logic: Logic.AND,
      filters: [
        {
          field: 'Email',
          operator: Operator.EQUAL,
          value: email,
        },
        {
          field: 'AppId',
          operator: Operator.EQUAL,
          value: this.appGetState().app.id,
        },
        {
          field: 'Created',
          operator: Operator.GTE,
          value: serverDateTime,
        },
      ],
    }

    const sort: IDataProviderSort = {
      field: 'Created',
      dir: 'desc',
    }
    const response = (await this.crud.read(filter, sort, 1, 1000, undefined, false, true)) || {}
    const rows = (response['Data'] as IObjectAny[]) || []
    return rows.map((row) => {
      return {
        id: row['EntryId'],
        email: row['Email'],
        event: row['Event'],
        value: parseInt(row['Value']) || 0,
        created: row['Created'],
      }
    })
  }

  public async persist(event: TUserEvent): Promise<TUserEvent> {
    const response = await this.crud.create([
      {
        Email: event.email,
        event: event.event,
        value: event.value.toString(),
        AppId: this.appGetState().app.id,
        Host: window.location.hostname,
        AudienceFilter: `AppId='${this.appGetState().app.id}' and Email='${event.email}'`,
      },
    ])
    if (response.length) {
      const row = response[0] as IObjectAny
      return createUserEvent({
        ...event,
        id: row['EntryId'],
        created: row['Created'],
        value: parseInt(row['Value']) || 0,
      })
    }

    throw new Error(`Cannot persist userEvent: ${JSON.stringify(event)}`)
  }
}
let instance: UserEventStorageServer
export const getUserEventStorageServer = () => {
  if (!instance) {
    instance = new UserEventStorageServer()
  }
  return instance
}

export default UserEventStorageServer
